import { IccClassificationApi } from '../icc-api'
import { IccCryptoXApi } from './icc-crypto-x-api'

import * as models from '../icc-api/model/models'

import { format as formatDate } from 'date-fns'
import { IccDataOwnerXApi } from './icc-data-owner-x-api'
import { AuthenticationProvider, NoAuthenticationProvider } from './auth/AuthenticationProvider'
import { SecureDelegation } from '../icc-api/model/SecureDelegation'
import { ShareMetadataBehaviour } from './crypto/ShareMetadataBehaviour'
import { ShareResult } from './utils/ShareResult'
import { EntityShareRequest } from '../icc-api/model/requests/EntityShareRequest'
import AccessLevelEnum = SecureDelegation.AccessLevelEnum
import RequestedPermissionEnum = EntityShareRequest.RequestedPermissionEnum
import { XHR } from '../icc-api/api/XHR'
import { EncryptedEntityXApi } from './basexapi/EncryptedEntityXApi'
import { EntityWithDelegationTypeName } from './utils'
import { SecretIdUseOption } from './crypto/SecretIdUseOption'

export class IccClassificationXApi extends IccClassificationApi implements EncryptedEntityXApi<models.Classification> {
  crypto: IccCryptoXApi
  dataOwnerApi: IccDataOwnerXApi

  get headers(): Promise<Array<XHR.Header>> {
    return super.headers.then((h) => this.crypto.accessControlKeysHeaders.addAccessControlKeysHeaders(h, EntityWithDelegationTypeName.Classification))
  }

  constructor(
    host: string,
    headers: { [key: string]: string },
    crypto: IccCryptoXApi,
    dataOwnerApi: IccDataOwnerXApi,
    private readonly autofillAuthor: boolean,
    authenticationProvider: AuthenticationProvider = new NoAuthenticationProvider(),
    fetchImpl: (input: RequestInfo, init?: RequestInit) => Promise<Response> = typeof window !== 'undefined'
      ? window.fetch
      : typeof self !== 'undefined'
      ? self.fetch
      : fetch
  ) {
    super(host, headers, authenticationProvider, fetchImpl)
    this.crypto = crypto
    this.dataOwnerApi = dataOwnerApi
  }

  /**
   * Creates a new instance of classification with initialised encryption metadata (not in the database).
   * @param user the current user.
   * @param patient the patient this classification refers to.
   * @param c initialised data for the classification. Metadata such as id, creation data, etc. will be automatically initialised, but you can specify
   * other kinds of data or overwrite generated metadata with this. You can't specify encryption metadata.
   * @param options optional parameters:
   * - additionalDelegates: delegates which will have access to the entity in addition to the current data owner and delegates from the
   * auto-delegations. Must be an object which associates each data owner id with the access level to give to that data owner. May overlap with
   * auto-delegations, in such case the access level specified here will be used.
   * - sfkOption: specifies which sfk of the owning entity to use.
   * - ignoreAutoDelegations: if true the data won't be shared with the autodelegations of the user, but only with additional delegates
   * - alternateRootDelegation: by default a new entity is created with a root delegation from self to self. In keyless mode this is not possible,
   * and instead the root delegation will be from self to another. You have to specify which delegate will be part of the root delegation.
   * @return a new instance of classification.
   */
  async newInstance(
    user: models.User,
    patient: models.Patient,
    c: any = {},
    options: {
      additionalDelegates?: { [dataOwnerId: string]: AccessLevelEnum }
      sfkOption?: SecretIdUseOption
      ignoreAutoDelegations?: boolean
      alternateRootDelegation?: string
    } = {}
  ): Promise<models.Classification> {
    const classification = {
      ...(c ?? {}),
      _type: 'org.taktik.icure.entities.Classification',
      id: c?.id ?? this.crypto.primitives.randomUuid(),
      created: c?.created ?? new Date().getTime(),
      modified: c?.modified ?? new Date().getTime(),
      responsible: c?.responsible ?? (this.autofillAuthor ? this.dataOwnerApi.getDataOwnerIdOf(user) : undefined),
      author: c?.author ?? (this.autofillAuthor ? user.id : undefined),
      codes: c?.codes ?? [],
      tags: c?.tags ?? [],
      healthElementId: c?.healthElementId ?? this.crypto.primitives.randomUuid(),
      openingDate: c?.openingDate ?? parseInt(formatDate(new Date(), 'yyyyMMddHHmmss')),
    }

    const ownerId = this.dataOwnerApi.getDataOwnerIdOf(user)
    if (ownerId !== (await this.dataOwnerApi.getCurrentDataOwnerId())) throw new Error('Can only initialise entities as current data owner.')
    const sfk = await this.crypto.xapi.resolveSecretIdUseOptions(
      { entity: patient, type: EntityWithDelegationTypeName.Patient },
      options.sfkOption ?? SecretIdUseOption.UseAnySharedWithParent
    )
    const extraDelegations = {
      ...(options.ignoreAutoDelegations == true
        ? {}
        : Object.fromEntries(
            [...(user.autoDelegations?.all ?? []), ...(user.autoDelegations?.medicalInformation ?? [])].map((d) => [d, AccessLevelEnum.WRITE])
          )),
      ...(options?.additionalDelegates ?? {}),
    }
    return new models.Classification(
      await this.crypto.xapi
        .entityWithInitialisedEncryptedMetadata(
          classification,
          EntityWithDelegationTypeName.Classification,
          patient?.id,
          sfk,
          true,
          extraDelegations,
          options.alternateRootDelegation
        )
        .then((x) => x.updatedEntity)
    )
  }

  /**
   * @deprecated use {@link findIdsBy} instead.
   */
  async findBy(hcpartyId: string, patient: models.Patient) {
    const extractedKeys = await this.crypto.xapi.secretIdsOf({ entity: patient, type: EntityWithDelegationTypeName.Patient }, hcpartyId)
    const topmostParentId = (await this.dataOwnerApi.getCurrentDataOwnerHierarchyIds())[0]
    return extractedKeys && extractedKeys.length > 0
      ? this.findClassificationsByHCPartyPatientForeignKeys(topmostParentId, [...new Set(extractedKeys)].join(','))
      : Promise.resolve([])
  }

  /**
   * Same as {@link findBy} but it will only return the ids of the classifications. It can also filter the classifications where
   * Classification.created is between startDate and endDate in ascending or descending order by that field (default: ascending).
   */
  async findIdsBy(hcpartyId: string, patient: models.Patient, startDate?: number, endDate?: number, descending?: boolean) {
    const extractedKeys = await this.crypto.xapi.secretIdsOf({ entity: patient, type: EntityWithDelegationTypeName.Patient }, hcpartyId)
    const topmostParentId = (await this.dataOwnerApi.getCurrentDataOwnerHierarchyIds())[0]
    return extractedKeys && extractedKeys.length > 0
      ? this.findClassificationIdsByDataOwnerPatientCreated(topmostParentId, [...new Set(extractedKeys)], startDate, endDate, descending)
      : Promise.resolve([])
  }

  /**
   * @param classification a classification
   * @return the id of the patient that the classification refers to, retrieved from the encrypted metadata. Normally there should only be one element
   * in the returned array, but in case of entity merges there could be multiple values.
   */
  async decryptPatientIdOf(classification: models.Classification): Promise<string[]> {
    return this.crypto.xapi.owningEntityIdsOf({ entity: classification, type: EntityWithDelegationTypeName.Classification }, undefined)
  }

  /**
   * @return if the logged data owner has write access to the content of the given classification
   */
  async hasWriteAccess(classification: models.Classification): Promise<boolean> {
    return this.crypto.xapi.hasWriteAccess({ entity: classification, type: EntityWithDelegationTypeName.Classification })
  }

  /**
   * Share an existing classification with other data owners, allowing them to access the non-encrypted data of the classification and optionally also
   * the encrypted content, with read-only or read-write permissions.
   * @param delegateId the id of the data owner which will be granted access to the classification.
   * @param classification the classification to share.
   * @param options optional parameters to customize the sharing behaviour:
   * - shareSecretIds: specifies which secret ids of the entity should be shared. If not provided all secret ids available to the current user will be shared
   * - shareEncryptionKey: specifies if the encryption key of the access log should be shared with the delegate, giving access to all encrypted
   * content of the entity, excluding other encrypted metadata (defaults to {@link ShareMetadataBehaviour.IF_AVAILABLE}). Note that by default a
   * classification does not have encrypted content.
   * - sharePatientId: specifies if the id of the patient that this classification refers to should be shared with the delegate (defaults to
   * {@link ShareMetadataBehaviour.IF_AVAILABLE}).
   * - requestedPermissions: the requested permissions for the delegate, defaults to {@link RequestedPermissionEnum.MAX_WRITE}.
   * @return the updated entity
   */
  async shareWith(
    delegateId: string,
    classification: models.Classification,
    options: {
      shareSecretIds?: string[]
      requestedPermissions?: RequestedPermissionEnum
      shareEncryptionKey?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
      sharePatientId?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
    } = {}
  ): Promise<models.Classification> {
    return this.shareWithMany(classification, { [delegateId]: options })
  }

  /**
   * Share an existing classification with other data owners, allowing them to access the non-encrypted data of the classification and optionally also
   * the encrypted content, with read-only or read-write permissions.
   * @param classification the classification to share.
   * @param delegates associates the id of data owners which will be granted access to the entity, to the following sharing options:
   * - shareSecretIds: specifies which secret ids of the entity should be shared. If not provided all secret ids available to the current user will be shared
   * - shareEncryptionKey: specifies if the encryption key of the access log should be shared with the delegate, giving access to all encrypted
   * content of the entity, excluding other encrypted metadata (defaults to {@link ShareMetadataBehaviour.IF_AVAILABLE}). Note that by default a
   * classification does not have encrypted content.
   * - sharePatientId: specifies if the id of the patient that this classification refers to should be shared with the delegate (defaults to
   * {@link ShareMetadataBehaviour.IF_AVAILABLE}).
   * - requestedPermissions: the requested permissions for the delegate, defaults to {@link RequestedPermissionEnum.MAX_WRITE}.
   * @return the updated entity
   */
  async shareWithMany(
    classification: models.Classification,
    delegates: {
      [delegateId: string]: {
        shareSecretIds?: string[]
        requestedPermissions?: RequestedPermissionEnum
        shareEncryptionKey?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
        sharePatientId?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
      }
    }
  ): Promise<models.Classification> {
    return (await this.tryShareWithMany(classification, delegates)).updatedEntityOrThrow
  }

  /**
   * Share an existing classification with other data owners, allowing them to access the non-encrypted data of the classification and optionally also
   * the encrypted content, with read-only or read-write permissions.
   * @param classification the classification to share.
   * @param delegates associates the id of data owners which will be granted access to the entity, to the following sharing options:
   * - shareSecretIds: specifies which secret ids of the entity should be shared. If not provided all secret ids available to the current user will be shared
   * - shareEncryptionKey: specifies if the encryption key of the access log should be shared with the delegate, giving access to all encrypted
   * content of the entity, excluding other encrypted metadata (defaults to {@link ShareMetadataBehaviour.IF_AVAILABLE}). Note that by default a
   * classification does not have encrypted content.
   * - sharePatientId: specifies if the id of the patient that this classification refers to should be shared with the delegate (defaults to
   * {@link ShareMetadataBehaviour.IF_AVAILABLE}).
   * - requestedPermissions: the requested permissions for the delegate, defaults to {@link RequestedPermissionEnum.MAX_WRITE}.
   * @return a promise which will contain the result of the operation: the updated entity if the operation was successful or details of the error if
   * the operation failed.
   */
  async tryShareWithMany(
    classification: models.Classification,
    delegates: {
      [delegateId: string]: {
        shareSecretIds?: string[]
        requestedPermissions?: RequestedPermissionEnum
        shareEncryptionKey?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
        sharePatientId?: ShareMetadataBehaviour // Defaults to ShareMetadataBehaviour.IF_AVAILABLE
      }
    }
  ): Promise<ShareResult<models.Classification>> {
    // All entities should have an encryption key.
    const entityWithEncryptionKey = await this.crypto.xapi.ensureEncryptionKeysInitialised(
      classification,
      EntityWithDelegationTypeName.Classification
    )
    const updatedEntity = entityWithEncryptionKey ? await this.modifyClassification(entityWithEncryptionKey) : classification
    return this.crypto.xapi.simpleShareOrUpdateEncryptedEntityMetadata(
      {
        entity: updatedEntity,
        type: EntityWithDelegationTypeName.Classification,
      },
      Object.fromEntries(
        Object.entries(delegates).map(([delegateId, options]) => [
          delegateId,
          {
            requestedPermissions: options.requestedPermissions,
            shareEncryptionKeys: options.shareEncryptionKey,
            shareOwningEntityIds: options.sharePatientId,
            shareSecretIds: options.shareSecretIds,
          },
        ])
      ),
      (x) => this.bulkShareClassifications(x)
    )
  }

  /**
   * Retrieves the data owners that have access to the given classification, along with their access levels.
   * @param entity the classification.
   * @return an object containing a map of data owner ids to their access levels, and a flag indicating if there are unknown anonymous data owners.
   */
  getDataOwnersWithAccessTo(
    entity: models.Classification
  ): Promise<{ permissionsByDataOwnerId: { [p: string]: AccessLevelEnum }; hasUnknownAnonymousDataOwners: boolean }> {
    return this.crypto.delegationsDeAnonymization.getDataOwnersWithAccessTo({ entity, type: EntityWithDelegationTypeName.Classification })
  }

  /**
   * Retrieves the encryption keys of the given classification.
   * @param entity the classification.
   * @return the encryption key ids.
   */
  getEncryptionKeysOf(entity: models.Classification): Promise<string[]> {
    return this.crypto.xapi.encryptionKeysOf({ entity, type: EntityWithDelegationTypeName.Classification }, undefined)
  }

  /**
   * Creates or updates de-anonymization metadata for the given classification, allowing the specified delegates to
   * identify the data owners that have access to it.
   * @param entity the classification.
   * @param delegates the data owner ids for which to create de-anonymization metadata.
   */
  createDelegationDeAnonymizationMetadata(entity: models.Classification, delegates: string[]): Promise<void> {
    return this.crypto.delegationsDeAnonymization.createOrUpdateDeAnonymizationInfo(
      { entity, type: EntityWithDelegationTypeName.Classification },
      delegates
    )
  }
}
